import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

// Auth Context
const AuthContext = createContext();

// Auth Actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS', 
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  LOGOUT: 'LOGOUT',
  REFRESH_TOKEN: 'REFRESH_TOKEN',
  UPDATE_USER: 'UPDATE_USER',
  SET_LOADING: 'SET_LOADING'
};

// Initial State
const initialState = {
  user: null,
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  loading: true,
  error: null
};

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
      return { ...state, loading: true, error: null };
      
    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.REGISTER_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        accessToken: action.payload.tokens.access_token,
        refreshToken: action.payload.tokens.refresh_token,
        loading: false,
        error: null
      };
      
    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
        error: action.payload
      };
      
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...initialState,
        loading: false
      };
      
    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        accessToken: action.payload.access_token
      };
      
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: action.payload
      };
      
    default:
      return state;
  }
};

// Token Management - Use environment variables to match api.js interceptor
const TOKEN_KEYS = {
  ACCESS_TOKEN: process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token',
  REFRESH_TOKEN: process.env.REACT_APP_REFRESH_TOKEN_KEY || 'draftjam_refresh_token',
  USER_DATA: process.env.REACT_APP_USER_DATA_KEY || 'draftjam_user_data'
};

const tokenManager = {
  setTokens: (accessToken, refreshToken, userData) => {
    console.log('[AuthContext] Setting tokens:', { 
      accessToken: !!accessToken, 
      refreshToken: !!refreshToken, 
      userData,
      accessTokenKey: TOKEN_KEYS.ACCESS_TOKEN,
      refreshTokenKey: TOKEN_KEYS.REFRESH_TOKEN,
      tokenPreview: accessToken ? `${accessToken.substring(0, 20)}...` : 'null'
    });
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
    console.log('[AuthContext] Tokens stored. Verifying:', {
      storedAccessToken: !!localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN),
      storedRefreshToken: !!localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN)
    });
  },
  
  getTokens: () => {
    const tokens = {
      accessToken: localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN),
      refreshToken: localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN),
      userData: JSON.parse(localStorage.getItem(TOKEN_KEYS.USER_DATA) || 'null')
    };
    console.log('Getting tokens:', { 
      accessToken: !!tokens.accessToken, 
      refreshToken: !!tokens.refreshToken, 
      userData: !!tokens.userData 
    }); // Debug log
    return tokens;
  },
  
  clearTokens: () => {
    console.log('Clearing tokens'); // Debug log
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.USER_DATA);
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { accessToken, refreshToken, userData } = tokenManager.getTokens();
        
        if (accessToken && refreshToken && userData) {
          console.log('Found stored tokens, verifying...'); // Debug log
          // Verify token is still valid by fetching user profile
          try {
            const response = await apiService.getCurrentUser(accessToken);
            console.log('Token verification successful:', response.data); // Debug log
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user: response.data,
                tokens: { access_token: accessToken, refresh_token: refreshToken }
              }
            });
          } catch (error) {
            console.error('Token verification failed:', error); // Debug log
            // Token might be expired, try to refresh
            if (error.response?.status === 401) {
              try {
                console.log('Attempting token refresh...'); // Debug log
                const refreshResponse = await apiService.refreshToken(refreshToken);
                const newAccessToken = refreshResponse.data.access_token;
                
                // Get user profile with new token
                const userResponse = await apiService.getCurrentUser(newAccessToken);
                
                // Update stored tokens
                tokenManager.setTokens(newAccessToken, refreshToken, userResponse.data);
                
                dispatch({
                  type: AUTH_ACTIONS.LOGIN_SUCCESS,
                  payload: {
                    user: userResponse.data,
                    tokens: { access_token: newAccessToken, refresh_token: refreshToken }
                  }
                });
              } catch (refreshError) {
                console.error('Token refresh failed:', refreshError); // Debug log
                // Refresh failed, clear tokens and logout
                tokenManager.clearTokens();
                dispatch({ type: AUTH_ACTIONS.LOGOUT });
              }
            } else {
              // Other error, clear tokens
              console.error('Other error during token verification:', error); // Debug log
              tokenManager.clearTokens();
              dispatch({ type: AUTH_ACTIONS.LOGOUT });
            }
          }
        } else {
          console.log('No stored tokens found'); // Debug log
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Auth Methods
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });
      
      const response = await apiService.login(credentials);
      console.log('Login response:', response.data); // Debug log
      
      // Handle the nested response structure from your backend
      const { user, tokens } = response.data;
      
      // Store tokens
      tokenManager.setTokens(tokens.access_token, tokens.refresh_token, user);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, tokens }
      });
      
      toast.success(`Welcome back, ${user.username}!`);
      return { success: true };
      
    } catch (error) {
      console.error('Login error:', error); // Debug log
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Login failed';
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage
      });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.REGISTER_START });
      
      const response = await apiService.register(userData);
      console.log('Register response:', response.data); // Debug log
      
      const { user, tokens } = response.data;
      
      // Store tokens
      tokenManager.setTokens(tokens.access_token, tokens.refresh_token, user);
      
      dispatch({
        type: AUTH_ACTIONS.REGISTER_SUCCESS,
        payload: { user, tokens }
      });
      
      toast.success(`Welcome to DraftJam, ${user.username}!`);
      return { success: true };
      
    } catch (error) {
      console.error('Register error:', error); // Debug log
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Registration failed';
      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: errorMessage
      });
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      console.log('AuthContext logout called, refreshToken:', !!state.refreshToken);
      if (state.refreshToken) {
        console.log('Calling logout API...');
        await apiService.logout(state.refreshToken);
        console.log('Logout API call successful');
      }
    } catch (error) {
      console.error('Logout API error:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      console.log('Clearing tokens and updating state...');
      tokenManager.clearTokens();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.success('Logged out successfully');
      console.log('Logout process completed');
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await apiService.updateProfile(profileData, state.accessToken);
      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: response.data
      });
      
      // Update stored user data
      const { refreshToken } = tokenManager.getTokens();
      tokenManager.setTokens(state.accessToken, refreshToken, response.data);
      
      toast.success('Profile updated successfully');
      return { success: true };
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Profile update failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const refreshAccessToken = async () => {
    try {
      if (!state.refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiService.refreshToken(state.refreshToken);
      const newAccessToken = response.data.access_token;
      
      dispatch({
        type: AUTH_ACTIONS.REFRESH_TOKEN,
        payload: { access_token: newAccessToken }
      });
      
      // Update stored access token
      const { refreshToken, userData } = tokenManager.getTokens();
      tokenManager.setTokens(newAccessToken, refreshToken, userData);
      
      return newAccessToken;
      
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      logout();
      throw error;
    }
  };

  // Verify token exists in localStorage before considering user authenticated
  // This prevents stale auth state when token is missing
  const verifiedIsAuthenticated = state.isAuthenticated && 
    !!localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) &&
    !!localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);

  const value = {
    ...state,
    isAuthenticated: verifiedIsAuthenticated, // Override with verified state
    login,
    register,
    logout,
    updateProfile,
    refreshAccessToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// HOC for protected components
export const withAuth = (WrappedComponent) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-slate-300">Loading...</div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-slate-300">Please log in to access this page.</div>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};